import { supabase } from "@/lib/supabase";
import { parseVolunteerHistory } from "@/lib/volunteerHistory";
import { assertAdmin, ADMIN_PAGE_SIZE } from "./adminGuard";
import { OutreachSendUnconfirmedError, ReenrichUnconfirmedError } from "@/services/errors";
import {
  classifyFunctionsError,
  describeFunctionsError,
  functionsFailureMessage,
  isIndeterminate,
} from "@/lib/supabaseFunctionsError";
import { ADMIN_SETTABLE_CANDIDATE_STATUSES, LINKEDIN_OUTREACH_STATUSES, DISMISSAL_REASONS } from "@/lib/types";
import type { LinkedinOutreachStatus, DismissalReason } from "@/lib/types";
import {
  countLeadImport,
  LEAD_IMPORT_GROUPS,
  LEAD_IMPORT_MAX_ROWS,
  LEAD_SOURCES,
} from "@/lib/leadTypes";
import type {
  AdminLead,
  AdminLeadFilters,
  LeadCreateInput,
  LeadImportOutcome,
  LeadImportReport,
  LeadImportRow,
  LeadImportRowResult,
  LeadSource,
  LeadSourceCandidate,
  LeadUpdateInput,
} from "@/lib/leadTypes";
import type {
  PaginatedResult,
  AdminQuestCandidate,
  CandidateAiAssessment,
  AdminCandidateFilters,
  AdminSettableCandidateStatus,
  HeadhunterRun,
  HeadhunterRunFilters,
  HeadhunterHealth,
  HeadhunterLogEvent,
  HeadhunterLogFilters,
  StoredTestbedCandidate,
  TestbedCandidateFilters,
  SearchTestbedCandidate,
  HeadhunterRunStats,
  HeadhunterMetricsFilters,
  HeadhunterRunKind,
  HeadhunterRunMetrics,
  HeadhunterCandidateFunnel,
  HeadhunterDropBreakdown,
  PendingQuest,
  PendingQuestFilters,
  QuestStatus,
  OutreachDraft,
  OutreachChannel,
  OutreachDraftFilters,
  DraftOutreachResult,
  ReenrichCandidateResult,
  TestbedOutreachResult,
  SendOutreachDraftResult,
  RunHeadhunterResult,
  CostBasis,
  SearchTestbedParams,
  SearchTestbedResult,
  SearchTestbedSpend,
  ParsedSearchQuery,
  OtherQuestMatch,
  SearchTargetType,
  CandidateUpdateInput,
  QuestCandidateCreateInput,
} from "@/lib/types";
// Runtime import (not type-only): thrown below to carry spend on a failed run.
import { SearchTestbedError } from "@/lib/types";
import type {
  CandidateDraftRefresh,
  IStaffingAdminService,
} from "@/services/interfaces/IStaffingAdminService";

// Shape returned by the admin_list_outreach_drafts RPC (snake_case columns).
interface OutreachDraftRow {
  id: string;
  candidate_id: string;
  quest_id: string;
  quest_title: string | null;
  email: string | null;
  full_name: string | null;
  location_country: string | null;
  subject: string;
  body: string;
  status: OutreachDraft["status"];
  kind: OutreachDraft["kind"];
  failure_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  // Candidate enrichment (DEV-379)
  candidate_score: number | null;
  candidate_skills: string[] | null;
  candidate_linkedin_url: string | null;
  candidate_source_url: string | null;
  candidate_employment_title: string | null;
  candidate_location_city: string | null;
  // Dismissal (DEV-380)
  candidate_dismissal_reason: string | null;
  candidate_dismissal_note: string | null;
  // AI profile (DEV-400)
  candidate_ai_profile: string | null;
  /** DEV-503: nullable here because a pre-DEV-503 RPC shape omits the column. */
  candidate_skills_from_query: boolean | null;
  // DEV-376: nullable/optional for the same deploy-skew reason as DEV-503 — the
  // app may run against an RPC shape that predates these columns.
  intro?: string | null;
  body_html?: string | null;
  body_override?: boolean | null;
  // DEV-788: optional for the same deploy-skew reason as the fields above.
  channel?: string | null;
  phone?: string | null;
}

export class SupabaseStaffingService implements IStaffingAdminService {
  // ─── Outreach review (DEV-351 R-B) ────────────────────
  async listOutreachDrafts(filters: OutreachDraftFilters): Promise<OutreachDraft[]> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_list_outreach_drafts", {
      p_chain_id: filters.chainId ?? null,
      p_status: filters.status ?? null,
      p_limit: filters.limit ?? ADMIN_PAGE_SIZE,
      p_offset: filters.offset ?? 0,
    });
    if (error) throw error;
    return ((data as OutreachDraftRow[]) ?? []).map((r) => ({
      id: r.id,
      candidateId: r.candidate_id,
      questId: r.quest_id,
      questTitle: r.quest_title,
      email: r.email,
      fullName: r.full_name,
      locationCountry: r.location_country,
      subject: r.subject,
      body: r.body,
      status: r.status,
      kind: r.kind,
      failureReason: r.failure_reason,
      reviewedBy: r.reviewed_by,
      reviewedAt: r.reviewed_at,
      sentAt: r.sent_at,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      candidateScore: r.candidate_score,
      candidateSkills: r.candidate_skills,
      candidateLinkedinUrl: r.candidate_linkedin_url,
      candidateSourceUrl: r.candidate_source_url,
      candidateEmploymentTitle: r.candidate_employment_title,
      candidateLocationCity: r.candidate_location_city,
      candidateDismissalReason: narrowDismissalReason(r.candidate_dismissal_reason),
      candidateDismissalNote: r.candidate_dismissal_note ?? null,
      candidateAiProfile: r.candidate_ai_profile ?? null,
      // DEV-503: `?? false` for the same reason as mapCandidateRow's — reading an
      // absent column as `true` during a deploy skew would relabel every genuine
      // vendor skill as "our search terms", discarding real evidence.
      candidateSkillsFromQuery: r.candidate_skills_from_query ?? false,
      // DEV-376: `?? false` on the latch for the same deploy-skew reason — an
      // absent column read as `true` would make every draft look hand-edited and
      // lock structured editing across the queue.
      intro: r.intro ?? null,
      bodyHtml: r.body_html ?? null,
      bodyOverride: r.body_override ?? false,
      // DEV-788: `?? "email"` mirrors the column DEFAULT, so a pre-migration
      // RPC shape reads as the email drafts those rows actually are rather
      // than as an unaddressable draft.
      channel: narrowOutreachChannel(r.channel),
      phone: r.phone ?? null,
    }));
  }

  async saveOutreachDraftFields(
    id: string,
    patch: { subject?: string; intro?: string; clearOverride?: boolean },
  ) {
    // NOT an RPC: the templates and the compliance copy live in the worker, and
    // duplicating them in the browser bundle is exactly what this hop avoids.
    await assertAdmin();
    const { data, error } = await supabase.functions.invoke("admin-save-draft-fields", {
      body: { draftId: id, ...patch },
    });
    if (error) {
      throw new Error(await describeFunctionsError(error));
    }
    return data as Awaited<ReturnType<IStaffingAdminService["saveOutreachDraftFields"]>>;
  }

  async saveOutreachDraftRaw(
    id: string,
    next: { subject: string; body: string; bodyHtml: string; expectedUpdatedAt: string },
  ): Promise<void> {
    // Verbatim bytes: nothing to render, so this goes straight to the RPC and
    // latches body_override so no later re-render can overwrite them.
    await assertAdmin();
    const { error } = await supabase.rpc("admin_save_outreach_draft", {
      p_id: id,
      p_subject: next.subject,
      p_body: next.body,
      p_body_html: next.bodyHtml,
      p_override: true,
      p_expected_updated_at: next.expectedUpdatedAt,
    });
    if (error) throw error;
  }

  async setOutreachDraftContact(
    id: string,
    contact: { channel: OutreachChannel; email: string | null; phone: string | null },
  ): Promise<void> {
    await assertAdmin();
    // Straight to the RPC: unlike the body-save paths there is no template to
    // render, and every rule that matters (admin gate, editable-status gate,
    // E.164 shape, and the re-queue on a recipient change) is enforced in SQL
    // where a browser cannot skip it.
    const { error } = await supabase.rpc("admin_set_outreach_draft_contact", {
      p_id: id,
      p_channel: contact.channel,
      p_email: contact.email,
      p_phone: contact.phone,
    });
    if (error) throw error;
  }

  async reviewOutreachDraft(
    id: string,
    decision: "approved" | "rejected",
  ): Promise<void> {
    await assertAdmin();
    // DEV-376: status only. The RPC also refuses to approve an external draft
    // whose body has lost either compliance link, and raises a typed error the
    // review panel surfaces.
    const { error } = await supabase.rpc("admin_review_outreach_draft", {
      p_id: id,
      p_decision: decision,
    });
    if (error) throw error;
  }

  async draftOutreachForCandidate(candidateId: string): Promise<DraftOutreachResult> {
    // On-demand draft: the admin-draft-outreach edge function verifies the JWT,
    // gates on profiles.role='admin', then forwards to the worker's single-
    // candidate draft path. NOT an RPC — rendering + guardrails live in the worker.
    const { data, error } = await supabase.functions.invoke("admin-draft-outreach", {
      body: { candidateId },
    });
    if (error) {
      // 4xx/5xx from the edge or worker (e.g. 404 not-found, 403 forbidden).
      throw new Error(await describeFunctionsError(error));
    }
    return data as DraftOutreachResult;
  }

  async createLeadFromCandidate(candidateId: string): Promise<string> {
    // admin_create_lead_from_candidate is admin-gated SECURITY DEFINER and
    // idempotent on the contact; its check_violation messages (suppressed
    // contact, no contact, already converted) are meant for the admin to read.
    const { data, error } = await supabase.rpc("admin_create_lead_from_candidate", {
      p_candidate_id: candidateId,
    });
    if (error) throw new Error(error.message);
    return data as string;
  }

  async createLeadManual(input: LeadCreateInput): Promise<string> {
    await assertAdmin();
    // DEV-819: the manual ingest path. admin_create_lead_manual refuses a blank
    // name, no contact, a non-E.164 phone, an email that already belongs to a
    // registered user (email-only: profiles has no phone column), a contact any
    // lead already holds, and a contact that has opted out — and every one of
    // those messages is written for the admin to read, so the raw message is
    // preserved rather than replaced with a generic failure. That is the whole
    // point of the ticket: a duplicate and a suppressed contact must arrive as
    // distinct, readable errors, not as an opaque 500.
    //
    // source and createdBy are NOT sent: the RPC hard-codes 'manual' and takes
    // the DID from requesting_user_id(), so a client cannot forge provenance.
    const { data, error } = await supabase.rpc("admin_create_lead_manual", {
      p_display_name: input.displayName,
      p_email: input.email,
      p_phone: input.phone,
      p_location_city: input.locationCity,
      p_location_country: input.locationCountry,
      p_employment_title: input.employmentTitle,
      p_skills: input.skills,
      p_notes: input.notes,
    });
    // THE POSTGRES ERROR IS PRESERVED AS `cause`, not just its message. The
    // message is written for a human and stays the toast's content, but it is
    // English prose: a caller that is not a toast — a script, an ops task, the
    // csv/outreach ingest paths that will land on this same port — can only tell
    // a duplicate (23505) from a policy refusal (23514) by reading `cause.code`.
    // Discarding it forced string-matching on English, which is what the verify
    // script's own `LIKE '%already claimed%'` had to resort to.
    // MockStaffingService attaches the same codes, so a caller that branches on
    // them behaves identically in mock mode. A non-admin never arrives here at
    // all — assertAdmin() above refuses before the request is sent.
    //
    // Assigned rather than passed to the constructor: the `{ cause }` overload is
    // ES2022 and this project targets ES2020 (TS2554).
    if (error) {
      const err = new Error(error.message);
      (err as Error & { cause?: unknown }).cause = error;
      throw err;
    }
    return data as string;
  }

  /**
   * DEV-820 — the CSV ingest path. One RPC, both modes.
   *
   * A REFUSED ROW IS NOT AN ERROR HERE, and that is the whole shape of this
   * method. `createLeadManual` above throws for its one row; this must not,
   * because the ticket's requirement is that a bad row does not abort the batch
   * — and an adapter that rejected on the first `suppressed` entry would discard
   * the 399 good rows before it while every database-side guarantee stayed
   * intact. Only a failure of the CALL rejects.
   */
  async importLeads(
    rows: LeadImportRow[],
    opts: { dryRun: boolean },
  ): Promise<LeadImportReport> {
    await assertAdmin();

    // REFUSED HERE AS WELL AS IN THE RPC. The function enforces the same cap, so
    // this is not the gate — it just avoids shipping a payload guaranteed to be
    // rejected, and gives the admin a message that names the count instead of a
    // Postgres one that arrives after the upload.
    if (rows.length > LEAD_IMPORT_MAX_ROWS) {
      throw new Error(
        `This import has ${rows.length} rows; the importer takes up to ${LEAD_IMPORT_MAX_ROWS} at a time — split the file and import it in parts.`,
      );
    }

    // `line` IS NOT SENT. The RPC indexes what it was handed, and the join back
    // to CSV line numbers happens below — see the `line` doc on LeadImportRow
    // for why the client owns them. Neither is `source` or `createdBy`: the RPC
    // sets 'csv' and requesting_user_id() itself, so a client cannot forge
    // provenance.
    const { data, error } = await supabase.rpc("admin_import_leads", {
      p_rows: rows.map(({ input }) => ({
        display_name: input.displayName,
        email: input.email,
        phone: input.phone,
        location_city: input.locationCity,
        location_country: input.locationCountry,
        employment_title: input.employmentTitle,
        skills: input.skills,
        notes: input.notes,
      })),
      p_dry_run: opts.dryRun,
    });

    // The Postgres error is preserved as `cause` for the same reason
    // createLeadManual preserves it: the message is English prose written for a
    // human, and a caller that is not a toast can only tell a policy refusal
    // (23514) from anything else by reading the SQLSTATE. Assigned rather than
    // passed to the constructor — the `{ cause }` overload is ES2022 and this
    // project targets ES2020 (TS2554).
    if (error) {
      const err = new Error(error.message);
      (err as Error & { cause?: unknown }).cause = error;
      throw err;
    }

    return buildLeadImportReport(rows, data as unknown[] | null, opts.dryRun);
  }

  // ─── DEV-817: the Leads admin view ─────────────────────────────────────────
  //
  // READS GO STRAIGHT TO THE TABLE. `leads` already has the exact gate this
  // feature needs — `leads_select_admin` is
  // `FOR SELECT TO authenticated USING (public.is_admin())` — so PostgREST
  // serves the search, both filters, the ordering and an exact count. A read RPC
  // would be a second read surface to secure for no capability gain.
  //
  // WRITES CANNOT. There is deliberately no authenticated write policy on the
  // table (20260907150000's threat model: that absence is what keeps unclaimed
  // leads out of every client-reachable path), so the three writers below are
  // admin-gated SECURITY DEFINER RPCs.

  async listLeads(filters: AdminLeadFilters): Promise<PaginatedResult<AdminLead>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    let query = supabase
      .from("leads")
      .select(
        "id, email, phone, display_name, location_city, location_country, skills, " +
          "employment_title, source, source_candidate_id, candidate_identity_id, " +
          "created_by, notes, claimed_by, claimed_at, created_at, updated_at",
        { count: "exact" },
      )
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    const search = filters.search?.trim();
    if (search) {
      // Escaped before it reaches `ilike`, as SupabaseAdminService.listUsers
      // does: an unescaped `%` or `_` from an admin pasting a contact turns the
      // lookup into a wildcard scan over PII and quietly returns strangers.
      const escaped = search.replace(/[%_\\]/g, "\\$&");
      // Contact FIRST, because "do we already hold a lead for this person?" is
      // asked with an email or a phone far more often than with a name.
      query = query.or(
        `email.ilike.%${escaped}%,phone.ilike.%${escaped}%,display_name.ilike.%${escaped}%`,
      );
    }

    if (filters.source) query = query.eq("source", filters.source);
    // Claim state is the axis every affordance in the view branches on, so it is
    // filtered here rather than post-fetch — a page of 50 claimed rows must not
    // render as an empty "unclaimed" list.
    if (filters.claim === "claimed") query = query.not("claimed_by", "is", null);
    else if (filters.claim === "unclaimed") query = query.is("claimed_by", null);

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list leads: ${error.message}`);

    // Through `unknown`, because the untyped client widens a `.or()`-filtered
    // select to `GenericStringError[]` and tsc refuses the direct assertion.
    const rows = (data ?? []) as unknown as Record<string, unknown>[];
    // `count: "exact"` gives a real total, so this list can state it honestly
    // rather than approximating from `hasMore` the way the candidate RPCs must.
    const total = count ?? rows.length;
    const nextOffset = offset + limit;

    return {
      data: rows.map(mapLeadRow),
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async getLeadSourceCandidate(leadId: string): Promise<LeadSourceCandidate | null> {
    await assertAdmin();
    // NOT a PostgREST embed on the list query. 20260623140000 revoked
    // authenticated SELECT on quest_candidates and scoped its row policy to the
    // QUEST'S CREATOR, so an admin embedding it gets nothing — and gets it
    // silently, which for a provenance link is the worst possible failure.
    const { data, error } = await supabase.rpc("admin_lead_source_candidate", {
      p_lead_id: leadId,
    });
    if (error) throw new Error(`Failed to resolve lead provenance: ${error.message}`);

    const row = (data as Record<string, unknown>[] | null)?.[0];
    // Zero rows is an ORDINARY answer: a manual/csv lead has no candidate, and
    // source_candidate_id is ON DELETE SET NULL because the retention sweep
    // purges the candidate row while the lead lives on.
    if (!row) return null;

    return {
      candidateId: row.candidate_id as string,
      questId: (row.quest_id as string | null) ?? null,
      questTitle: (row.quest_title as string | null) ?? null,
      candidateName: (row.candidate_name as string | null) ?? null,
      candidateStatus: (row.candidate_status as string | null) ?? null,
    };
  }

  async updateLead(id: string, input: LeadUpdateInput): Promise<void> {
    await assertAdmin();
    // admin_update_lead refuses a claimed lead, a blank name, a non-E.164 phone,
    // a contact another lead holds and a contact that has opted out. Every one of
    // those messages is written for the admin to read, so the raw message is
    // preserved rather than replaced with a generic failure.
    const { error } = await supabase.rpc("admin_update_lead", {
      p_id: id,
      p_display_name: input.displayName,
      p_email: input.email,
      p_phone: input.phone,
      p_location_city: input.locationCity,
      p_location_country: input.locationCountry,
      p_employment_title: input.employmentTitle,
      p_skills: input.skills,
      p_notes: input.notes,
    });
    if (error) throw new Error(error.message);
  }

  async deleteLead(id: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_delete_lead", { p_id: id });
    if (error) throw new Error(error.message);
  }

  async sendTestbedOutreach(input: {
    testbedCandidateId: string;
    channel: "email" | "manual" | "sms";
    subject: string;
    body: string;
  }): Promise<TestbedOutreachResult> {
    // Manual testbed outreach: the admin-testbed-outreach edge function verifies
    // the JWT, gates on profiles.role='admin', then forwards to the worker, which
    // re-checks R6 suppression and R8 cooldown and appends the compliance footer.
    // subject/body are the admin's own text and cross verbatim. NOT an RPC — the
    // send, the guardrails and the opt-out token all live in the worker.
    //
    // createdBy is NOT sent: the edge function takes it from the verified token,
    // so a client cannot attribute a send to another admin.
    const { data, error } = await supabase.functions.invoke("admin-testbed-outreach", {
      body: input,
    });
    if (error) {
      // 4xx/5xx from the edge or worker (e.g. 404 not-found, 403 forbidden).
      throw new Error(await describeFunctionsError(error));
    }
    return data as TestbedOutreachResult;
  }

  async sendOutreachDraft(draftId: string): Promise<SendOutreachDraftResult> {
    // DEV-795: send one approved draft now rather than waiting for the worker's
    // 6h dispatch sweep. The admin-send-draft edge function verifies the JWT,
    // gates on profiles.role='admin', then forwards to the worker, which
    // re-checks R6 suppression and R8 cooldown and refuses a non-approved draft.
    //
    // Only the id crosses: the text that is sent is the reviewed text on the
    // row, so an approved draft can never be swapped for unreviewed copy here.
    const { data, error } = await supabase.functions.invoke("admin-send-draft", {
      body: { draftId },
    });
    if (error) {
      // An indeterminate failure is NOT a failed send. Two shapes reach here:
      // no response at all (fetch rejected), and our own proxy's 502/504, which
      // it returns after giving up on a worker that is still running. In both
      // the email may already have gone out, so reporting a flat failure tells
      // the admin the opposite of what is known. Everything else — a 403, a
      // 404, the worker's own typed refusals — is a definite answer.
      const failure = await classifyFunctionsError(error);
      if (isIndeterminate(failure)) throw new OutreachSendUnconfirmedError(draftId);
      throw new Error(functionsFailureMessage(failure));
    }
    return data as SendOutreachDraftResult;
  }

  async reenrichCandidate(candidateId: string): Promise<ReenrichCandidateResult> {
    // On-demand re-enrich: the admin-reenrich-candidate edge function verifies
    // the JWT, gates on profiles.role='admin', then forwards to the worker's
    // single-candidate resolve→enrich→verify path. NOT an RPC — the paid
    // lookup + persistence live in the worker.
    const { data, error } = await supabase.functions.invoke("admin-reenrich-candidate", {
      body: { candidateId },
    });
    if (error) {
      // Same distinction the send path makes, and here it is money rather than
      // compliance: our proxy gives up at WORKER_FORWARD_PAID_TIMEOUT_MS but the
      // worker keeps going and keeps spending, so an admin told "it failed"
      // buys the same paid lookup a second time.
      const failure = await classifyFunctionsError(error);
      if (isIndeterminate(failure)) throw new ReenrichUnconfirmedError(candidateId);
      throw new Error(functionsFailureMessage(failure));
    }
    return data as ReenrichCandidateResult;
  }

  async runHeadhunterForQuest(
    questId: string,
    opts?: { forceReunderstand?: boolean; skipInternal?: boolean; skipExternal?: boolean; platforms?: string[]; targetType?: SearchTargetType },
  ): Promise<RunHeadhunterResult> {
    // Manual run: the admin-run-headhunter edge function verifies the JWT,
    // gates on profiles.role='admin', then forwards to the worker's forced
    // run path. NOT an RPC — the funnel + guardrails live in the worker.
    const { data, error } = await supabase.functions.invoke("admin-run-headhunter", {
      body: {
        questId,
        forceReunderstand: opts?.forceReunderstand ?? false,
        skipInternal: opts?.skipInternal ?? false,
        skipExternal: opts?.skipExternal ?? false,
        ...(opts?.platforms ? { platforms: opts.platforms } : {}),
        // DEV-579: omitted, not sent as undefined — absence means "let the
        // model decide", which the worker's precedence chain treats as a
        // distinct signal from an asserted "either".
        ...(opts?.targetType ? { targetType: opts.targetType } : {}),
      },
    });
    if (error) {
      // 4xx/5xx from the edge or worker (e.g. 404 quest not found, 403 forbidden).
      throw new Error(await describeFunctionsError(error));
    }
    return data as RunHeadhunterResult;
  }

  async searchCandidatesTestbed(params: SearchTestbedParams): Promise<SearchTestbedResult> {
    // Admin diagnostic-only: the admin-search-test edge function verifies the
    // JWT, gates on profiles.role='admin', then invokes StableEnrich directly
    // (real vendor spend) — no funnel, persistence, or scoring involved.
    const { data, error } = await supabase.functions.invoke("admin-search-test", {
      body: {
        titles: params.titles,
        skills: params.skills,
        location: params.location,
        limit: params.limit,
        // DEV-473/R4a: always an explicit array (defaults to `[]`), never
        // omitted, so the worker never falls through to "all platforms".
        platforms: params.platforms ?? [],
        // Omitted when absent, and previously not sent at all: `countryCode`
        // decides which local sites the Exa leg is restricted to.
        ...(params.countryCode ? { countryCode: params.countryCode } : {}),
        // Omitted when empty for the same reason countryCode is: an empty
        // value must not suppress the worker's own fallback.
        ...(params.targetType ? { targetType: params.targetType } : {}),
      },
    });
    if (error) {
      const failure = await classifyFunctionsError(error);
      // DEV-494: a run that failed may already have spent money — carry the
      // worker's spend report on the error instead of dropping it with the body.
      const spend =
        failure.kind === "http" ? (failure.body.spend as SearchTestbedSpend | undefined) : undefined;
      throw new SearchTestbedError(functionsFailureMessage(failure), spend);
    }
    return data as SearchTestbedResult;
  }

  async parseSearchQuery(query: string): Promise<ParsedSearchQuery> {
    const { data, error } = await supabase.functions.invoke("admin-parse-search-query", {
      body: { query },
    });
    if (error) {
      throw new Error(await describeFunctionsError(error));
    }
    return data as ParsedSearchQuery;
  }

  // ─── Headhunter / Staffing (DEV-351) ──────────────────────

  async listHeadhunterCandidates(
    filters: AdminCandidateFilters,
  ): Promise<PaginatedResult<AdminQuestCandidate>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    // SECURITY DEFINER RPC: bypasses the column REVOKE on email/phone (creator
    // view migration), so admins — and only admins (is_admin() guard) — see the
    // full sourced contact + provenance. Fetch one extra to detect hasMore.
    const { data, error } = await supabase.rpc("admin_list_quest_candidates", {
      p_chain_id: filters.chainId ?? null,
      p_quest_id: filters.questId ?? null,
      p_source: filters.source && filters.source !== "all" ? filters.source : null,
      p_status: filters.status && filters.status !== "all" ? filters.status : null,
      p_search: filters.search?.trim() || null,
      p_has_contact: filters.hasContact ?? null,
      p_unconverted: filters.unconverted ?? null,
      // W-C: match-tier (score range / unscored) + location/skill facets + sort.
      // The tier→range mapping is done client-side (candidateTiers.tierScoreFilter)
      // so the cut points aren't duplicated in SQL; the RPC applies the range as-is.
      p_min_score: filters.minScore ?? null,
      p_max_score: filters.maxScore ?? null,
      p_unscored: filters.unscored ?? null,
      p_location: filters.location?.trim() || null,
      p_skill: filters.skill?.trim() || null,
      p_sort: filters.sort ?? null,
      p_sort_dir: filters.sortDir ?? null,
      p_limit: limit + 1,
      p_offset: offset,
    });
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as HeadhunterCandidateRow[];
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;
    // total_count is a window count over the full filtered set (same on every
    // row); fall back to the page length on an empty result.
    const total = rows[0]?.total_count != null ? Number(rows[0].total_count) : page.length;
    return {
      data: page.map(mapCandidateRow),
      total,
      cursor: null,
      hasMore,
    };
  }

  // DEV-450: other-quest list for the candidate drawer's "+N other quests"
  // expansion, fetched on demand rather than bundled into the list RPC.
  async getOtherQuestsForIdentity(candidateIdentityId: string, excludeQuestId?: string): Promise<OtherQuestMatch[]> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_list_other_quests_for_identity", {
      p_candidate_identity_id: candidateIdentityId,
      p_exclude_quest_id: excludeQuestId ?? null,
    });
    if (error) throw new Error(error.message);
    return ((data ?? []) as { quest_id: string; quest_title: string | null; status: string }[]).map((r) => ({
      questId: r.quest_id,
      questTitle: r.quest_title,
      status: r.status as OtherQuestMatch["status"],
    }));
  }

  async getHeadhunterHealth(chainId: number): Promise<HeadhunterHealth | null> {
    await assertAdmin();
    const { data, error } = await supabase
      .from("headhunter_health")
      .select("*")
      .eq("chain_id", chainId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    const row = data as HeadhunterHealthRow;
    return {
      status: row.status,
      chainId: row.chain_id,
      walletBalanceUsdc: row.wallet_balance_usdc != null ? Number(row.wallet_balance_usdc) : null,
      dailySpendUsdc: row.daily_spend_usdc != null ? Number(row.daily_spend_usdc) : null,
      dailyCapUsdc: row.daily_cap_usdc != null ? Number(row.daily_cap_usdc) : null,
      version: row.version,
      lastError: row.last_error,
      lastSeenAt: row.last_seen_at,
      updatedAt: row.updated_at,
    };
  }

  async setCandidateStatus(
    id: string,
    status: AdminSettableCandidateStatus,
    dismissalReason?: DismissalReason | null,
    dismissalNote?: string | null,
  ): Promise<{
    status: AdminSettableCandidateStatus;
    outreachSentAt: string | null;
    dismissalReason: DismissalReason | null;
    dismissalNote: string | null;
  }> {
    await assertAdmin();
    // SECURITY DEFINER RPC — the only write path: quest_candidates has no
    // authenticated UPDATE policy. Returns the persisted state (incl. the
    // outreach_sent_at the RPC may have stamped when status flipped to 'invited').
    // The RPC returns TABLE — avoid .single() which adds the
    // Accept: application/vnd.pgrst.object+json header and triggers
    // "Cannot coerce the result to a single JSON object" on SETOF returns.
    const { data, error } = await supabase
      .rpc("admin_set_candidate_status", {
        p_id: id,
        p_status: status,
        p_dismissal_reason: dismissalReason ?? null,
        p_dismissal_note: dismissalNote ?? null,
      });
    if (error) throw new Error(error.message);
    const rows = data as {
      status: string;
      outreach_sent_at: string | null;
      dismissal_reason: string | null;
      dismissal_note: string | null;
    }[];
    const row = rows?.[0];
    if (!row) throw new Error("admin_set_candidate_status returned no rows");
    // The RPC only ever returns an admin-settable status, but it shares the row
    // with the conversion flow's 'applied' — validate before trusting the cast so
    // a concurrent flip can't smuggle an off-contract value into the UI.
    if (!(ADMIN_SETTABLE_CANDIDATE_STATUSES as readonly string[]).includes(row.status)) {
      throw new Error(`Unexpected candidate status from RPC: ${row.status}`);
    }
    return {
      status: row.status as AdminSettableCandidateStatus,
      outreachSentAt: row.outreach_sent_at ?? null,
      dismissalReason: narrowDismissalReason(row.dismissal_reason),
      dismissalNote: row.dismissal_note ?? null,
    };
  }

  async inviteMemberToVolunteer(
    candidateId: string,
  ): Promise<{ proposalId: string; taskId: string; created: boolean }> {
    await assertAdmin();
    // SECURITY DEFINER, is_admin()-gated (20260922140000). It writes the
    // creator-side $0 invite, marks the candidate invited and sends the one
    // volunteer_invite notification. Every refusal (external candidate, paid
    // quest, opted out, no open spot) is a message the admin needs to read, so
    // it is thrown as-is.
    const { data, error } = await supabase
      .rpc("admin_invite_member_to_volunteer", { p_candidate_id: candidateId });
    if (error) throw new Error(error.message);
    const row = (data as { proposal_id: string; task_id: string; created: boolean }[])?.[0];
    if (!row) throw new Error("admin_invite_member_to_volunteer returned no rows");
    return { proposalId: row.proposal_id, taskId: row.task_id, created: row.created };
  }

  async countVolunteerAlertRecipients(questId: string): Promise<number> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_volunteer_alert_recipients", {
      p_quest_id: questId,
    });
    if (error) throw new Error(error.message);
    return (data as number | null) ?? 0;
  }

  async sendVolunteerQuestAlert(questId: string): Promise<{ sent: number; skipped: number }> {
    await assertAdmin();
    // SECURITY DEFINER, is_admin()-gated, deduplicated per member
    // (20260923120000). Every refusal (paid quest, closed quest) is a message
    // the admin needs to read, so it is thrown as-is.
    const { data, error } = await supabase
      .rpc("admin_send_volunteer_quest_alert", { p_quest_id: questId });
    if (error) throw new Error(error.message);
    const row = (data as { sent: number; skipped: number }[])?.[0];
    if (!row) throw new Error("admin_send_volunteer_quest_alert returned no rows");
    return { sent: row.sent, skipped: row.skipped };
  }

  async addQuestCandidate(input: QuestCandidateCreateInput): Promise<string> {
    await assertAdmin();
    // DEV-TBD: the manual ingest path for quest candidates.
    // admin_add_quest_candidate refuses an unknown quest, a blank name, no
    // contact at all, a non-E.164 phone, and a duplicate identity this quest
    // already holds — and every one of those messages is written for the admin
    // to read, so the raw message is preserved rather than replaced with a
    // generic failure.
    //
    // source, status, lawful_basis, collected_at, source_url, and manually_added
    // are NOT sent: the RPC hard-codes them, so a client cannot forge provenance
    // or mark a sweep-sourced row as manual.
    const { data, error } = await supabase.rpc("admin_add_quest_candidate", {
      p_quest_id: input.questId,
      p_full_name: input.fullName,
      p_email: input.email ?? null,
      p_phone: input.phone ?? null,
      p_linkedin_url: input.linkedinUrl ?? null,
      p_location_city: input.locationCity ?? null,
      p_location_country: input.locationCountry ?? null,
      p_employment_title: input.employmentTitle ?? null,
      p_skills: input.skills ?? [],
      p_notes: input.notes ?? null,
    });
    // THE POSTGRES ERROR IS PRESERVED AS `cause`, not just its message. The
    // message is written for a human and stays the toast's content, but it is
    // English prose: a caller that is not a toast can only tell a duplicate
    // (23505) from a policy refusal (23514) by reading `cause.code`.
    // MockStaffingService attaches the same codes, so a caller that branches on
    // them behaves identically in mock mode.
    if (error) {
      const err = new Error(error.message);
      (err as Error & { cause?: unknown }).cause = error;
      throw err;
    }
    return data as string;
  }

  async setSourceUrls(candidateId: string, urls: string[]): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_set_candidate_source_urls", {
      p_id: candidateId,
      p_urls: urls,
    });
    if (error) throw new Error(error.message);
  }

  async setCandidateResumeUrl(id: string, url: string | null): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_set_candidate_resume_url", {
      p_id: id,
      p_resume_url: url,
    });
    if (error) throw new Error(error.message);
  }

  async setCandidateEmail(id: string, email: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_set_candidate_email", {
      p_id: id,
      p_email: email,
    });
    if (error) throw new Error(error.message);
  }

  async updateCandidate(id: string, input: CandidateUpdateInput): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_update_quest_candidate", {
      p_id: id,
      p_full_name: input.fullName ?? null,
      p_email: input.email ?? null,
      p_phone: input.phone ?? null,
      p_linkedin_url: input.linkedinUrl ?? null,
      p_employment_title: input.employmentTitle ?? null,
      p_location_city: input.locationCity ?? null,
      p_skills: input.skills ?? [],
    });
    if (error) throw new Error(error.message);
  }

  async refreshCandidateDrafts(candidateId: string): Promise<CandidateDraftRefresh> {
    // NOT an RPC, for the same reason saveOutreachDraftFields is not: the
    // templates and the compliance copy live in the worker container, and the
    // browser bundle must never hold a second copy of them.
    await assertAdmin();
    const { data, error } = await supabase.functions.invoke("admin-refresh-candidate-drafts", {
      body: { candidateId },
    });
    if (error) throw new Error(await describeFunctionsError(error));
    const r = (data ?? {}) as { rerendered?: unknown; staleBodies?: unknown };
    // Coerced rather than trusted: a worker that predates this shape must read
    // as "nothing was refreshed", never as NaN in a toast.
    return {
      rerendered: typeof r.rerendered === "number" ? r.rerendered : 0,
      staleBodies: typeof r.staleBodies === "number" ? r.staleBodies : 0,
    };
  }

  async clearQuestCandidates(
    questId: string,
    source: "internal" | "external",
  ): Promise<number> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_clear_quest_candidates", {
      p_quest_id: questId,
      p_source: source,
    });
    if (error) throw new Error(error.message);
    // The RPC returns the deleted row count; coerce defensively so a null from
    // an older deployment reads as "nothing deleted" rather than NaN in the toast.
    return typeof data === "number" ? data : 0;
  }

  async updateTestbedCandidate(id: string, input: CandidateUpdateInput): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_update_testbed_candidate", {
      p_id: id,
      p_full_name: input.fullName ?? null,
      p_email: input.email ?? null,
      p_phone: input.phone ?? null,
      p_linkedin_url: input.linkedinUrl ?? null,
      p_employment_title: input.employmentTitle ?? null,
      p_location_city: input.locationCity ?? null,
      p_skills: input.skills ?? [],
    });
    if (error) throw new Error(error.message);
  }

  async generateCandidateProfile(
    candidateId: string,
  ): Promise<{ profile: string; generatedAt: string }> {
    await assertAdmin();
    const { data, error } = await supabase.functions.invoke(
      "admin-generate-candidate-profile",
      { body: { candidateId } },
    );
    if (error) {
      throw new Error(await describeFunctionsError(error));
    }
    return data as { profile: string; generatedAt: string };
  }

  async generateCandidateAssessment(
    candidateId: string,
  ): Promise<{ assessment: CandidateAiAssessment; generatedAt: string }> {
    await assertAdmin();
    const { data, error } = await supabase.functions.invoke(
      "admin-generate-candidate-assessment",
      { body: { candidateId } },
    );
    if (error) {
      throw new Error(await describeFunctionsError(error));
    }
    return data as { assessment: CandidateAiAssessment; generatedAt: string };
  }

  async generateOutreachMessage(
    draftId: string,
  ): Promise<{ paragraph: string; generatedAt: string }> {
    await assertAdmin();
    const { data, error } = await supabase.functions.invoke(
      "admin-generate-outreach-message",
      { body: { draftId } },
    );
    if (error) {
      throw new Error(await describeFunctionsError(error));
    }
    return data as { paragraph: string; generatedAt: string };
  }

  async clearCandidateAiField(id: string, field: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_clear_candidate_ai_field", {
      p_id: id,
      p_field: field,
    });
    if (error) throw new Error(error.message);
  }

  async listHeadhunterRuns(
    filters: HeadhunterRunFilters,
  ): Promise<PaginatedResult<HeadhunterRun>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    let query = supabase
      .from("headhunter_runs")
      .select("*", { count: "exact" })
      .order("started_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (filters.chainId != null) {
      query = query.eq("chain_id", filters.chainId);
    }
    if (filters.status && filters.status !== "all") {
      query = query.eq("status", filters.status);
    }
    if (filters.kind && filters.kind !== "all") {
      query = query.eq("kind", filters.kind);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as HeadhunterRunRow[];
    return {
      data: rows.map(mapRunRow),
      total: count ?? rows.length,
      cursor: null,
      hasMore: count != null ? offset + rows.length < count : false,
    };
  }

  async listHeadhunterLogEvents(
    filters: HeadhunterLogFilters,
  ): Promise<PaginatedResult<HeadhunterLogEvent>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    // Drill-down (runId set) reads a single run's events oldest-first — a
    // chronological timeline. The live tail (no runId) reads newest-first.
    const drillDown = filters.runId != null;
    let query = supabase
      .from("headhunter_log_events")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: drillDown })
      .range(offset, offset + limit - 1);

    if (drillDown) query = query.eq("run_id", filters.runId!);
    if (filters.chainId != null) query = query.eq("chain_id", filters.chainId);
    if (filters.level && filters.level !== "all") query = query.eq("level", filters.level);
    // DEV-535: kind-scope the live tail. Filters the event's OWN denormalised
    // column, so events from an in-flight run (whose headhunter_runs row is not
    // written until the run ends) and orphan events still appear — a join to
    // headhunter_runs would silently drop exactly those. "all" adds no predicate,
    // matching admin_headhunter_run_metrics' p_kind = 'all' branch.
    if (filters.kind && filters.kind !== "all") query = query.eq("kind", filters.kind);

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as HeadhunterLogEventRow[];
    return {
      data: rows.map(mapLogEventRow),
      total: count ?? rows.length,
      cursor: null,
      hasMore: count != null ? offset + rows.length < count : false,
    };
  }

  /**
   * DEV-562: one testbed run's stored candidates, in funnel order.
   *
   * A direct table read rather than an RPC, matching listHeadhunterLogEvents:
   * the table's admin-only SELECT policy IS the gate, there is nothing to
   * aggregate, and an RPC would add a signature to keep in sync for no benefit.
   */
  async listTestbedRunCandidates(
    filters: TestbedCandidateFilters,
  ): Promise<PaginatedResult<StoredTestbedCandidate>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    const { data, error, count } = await supabase
      .from("headhunter_testbed_candidates")
      .select("id, run_id, ordinal, payload", { count: "exact" })
      // Funnel order, not insertion time: the whole array lands in one
      // statement and shares a created_at, so ordinal is the only ordering
      // that survives the write.
      .eq("run_id", filters.runId)
      .order("ordinal", { ascending: true })
      .range(offset, offset + limit - 1);

    if (error) throw new Error(error.message);
    const rows = (data ?? []) as TestbedCandidateRow[];
    return {
      data: rows.map((row) => ({
        id: row.id,
        runId: row.run_id,
        ordinal: row.ordinal,
        candidate: { ...(row.payload || {}), id: row.id },
      })),
      total: count ?? rows.length,
      cursor: null,
      hasMore: count != null ? offset + rows.length < count : false,
    };
  }

  async markLinkedinSent(candidateId: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_mark_linkedin_sent", {
      p_candidate_id: candidateId,
    });
    if (error) throw new Error(`markLinkedinSent failed: ${error.message}`);
  }

  async markLinkedinOptedOut(candidateId: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_mark_linkedin_opted_out", {
      p_candidate_id: candidateId,
    });
    if (error) throw new Error(`markLinkedinOptedOut failed: ${error.message}`);
  }

  async listPendingQuests(
    filters: PendingQuestFilters,
  ): Promise<PaginatedResult<PendingQuest>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    const { data, error } = await supabase.rpc("admin_list_pending_quests", {
      p_chain_id: filters.chainId ?? null,
      p_search: filters.search?.trim() || null,
      p_limit: limit + 1,
      p_offset: offset,
    });
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as PendingQuestRow[];
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;
    const total = rows[0]?.row_count != null ? Number(rows[0].row_count) : page.length;
    return {
      data: page.map(mapPendingQuestRow),
      total,
      cursor: null,
      hasMore,
    };
  }

  async getHeadhunterRunStats(
    chainId: number,
    kind: HeadhunterRunKind | "all" = "quest",
  ): Promise<HeadhunterRunStats> {
    await assertAdmin();
    // Single aggregate query — no RPC needed, headhunter_runs has admin-only
    // SELECT policy so this is safe. Scoped to one network so testnet (Amoy)
    // runs never inflate the mainnet (Polygon) totals shown in the panel.
    let query = supabase
      .from("headhunter_runs")
      .select("spend_usdc, internal_found, external_found, contactable_found, candidates_persisted, started_at")
      .eq("chain_id", chainId)
      .order("started_at", { ascending: true });
    // DEV-520: the same reasoning the chain filter already documents — a
    // diagnostic run must never inflate the totals shown for real work.
    if (kind !== "all") query = query.eq("kind", kind);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as {
      spend_usdc: number;
      internal_found: number;
      external_found: number;
      contactable_found: number;
      candidates_persisted: number;
      started_at: string;
    }[];
    return {
      totalRuns: rows.length,
      totalSpendUsdc: rows.reduce((sum, r) => sum + Number(r.spend_usdc ?? 0), 0),
      totalInternalFound: rows.reduce((sum, r) => sum + Number(r.internal_found ?? 0), 0),
      totalExternalFound: rows.reduce((sum, r) => sum + Number(r.external_found ?? 0), 0),
      totalContactableFound: rows.reduce((sum, r) => sum + Number(r.contactable_found ?? 0), 0),
      totalCandidatesPersisted: rows.reduce((sum, r) => sum + Number(r.candidates_persisted ?? 0), 0),
      firstRunAt: rows.length > 0 ? rows[0]!.started_at : null,
      lastRunAt: rows.length > 0 ? rows[rows.length - 1]!.started_at : null,
    };
  }

  // ─── Headhunter analytics (DEV-363) ─────────────────────
  // Three SECURITY DEFINER + is_admin() RPCs that return camelCase JSONB
  // matching the domain types verbatim — no snake→camel mapping needed.

  async getHeadhunterRunMetrics(
    filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterRunMetrics> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_headhunter_run_metrics", {
      p_chain_id: filters.chainId,
      p_from: filters.from ?? null,
      p_to: filters.to ?? null,
      p_quest_id: filters.questId ?? null,
      p_kind: filters.kind ?? "quest",
    });
    if (error) throw error;
    return data as HeadhunterRunMetrics;
  }

  async getHeadhunterCandidateFunnel(
    filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterCandidateFunnel> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_headhunter_candidate_funnel", {
      p_chain_id: filters.chainId,
      p_from: filters.from ?? null,
      p_to: filters.to ?? null,
      p_quest_id: filters.questId ?? null,
      p_source: filters.source && filters.source !== "all" ? filters.source : null,
    });
    if (error) throw error;
    return data as HeadhunterCandidateFunnel;
  }

  async getHeadhunterDropBreakdown(
    filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterDropBreakdown> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_headhunter_drop_breakdown", {
      p_chain_id: filters.chainId,
      p_from: filters.from ?? null,
      p_to: filters.to ?? null,
      p_quest_id: filters.questId ?? null,
    });
    if (error) throw error;
    return data as HeadhunterDropBreakdown;
  }
}

// ─── Headhunter row types + mappers ─────────────────────────

interface HeadhunterCandidateRow {
  id: string;
  quest_id: string;
  quest_title: string | null;
  source: "internal" | "external";
  member_id: string | null;
  converted_user_id: string | null;
  full_name: string | null;
  location_city: string | null;
  location_country: string | null;
  skills: string[] | null;
  employment_title: string | null;
  linkedin_url: string | null;
  email: string | null;
  phone: string | null;
  email_verified: boolean | null;
  score: number | null;
  status: AdminQuestCandidate["status"];
  outreach_sent_at: string | null;
  source_url: string | null;
  collected_at: string | null;
  lawful_basis: string | null;
  purpose: string | null;
  last_refreshed_at: string | null;
  created_at: string | null;
  linkedin_outreach_status: AdminQuestCandidate["linkedinOutreachStatus"] | null;
  linkedin_sent_at: string | null;
  dismissal_reason: string | null;
  dismissal_note: string | null;
  source_urls: string[] | null;
  ai_profile: string | null;
  ai_profile_generated_at: string | null;
  ai_assessment: CandidateAiAssessment | null;
  ai_assessment_generated_at: string | null;
  resume_url: string | null;
  ai_enriched_fields: string[];
  candidate_identity_id: string | null;
  other_quest_count: number | null;
  /** DEV-473: the single platform this candidate was sourced from (e.g.
   * "facebook", "github"), or null for FullEnrich/internal sources. */
  source_platform: string | null;
  /** DEV-481: per-platform public profile URLs, or null. */
  social_profiles: Record<string, string> | null;
  /** DEV-474: "person" | "business". Null on rows predating the column, which the
   * mapper reads as "person" to match the DB DEFAULT. */
  entity_type: string | null;
  /** DEV-474: Google Places id for business rows; null for people. */
  maps_place_id: string | null;
  /** DEV-499b: Google Maps formatted address for business rows; null for people. */
  formatted_address: string | null;
  /** DEV-499b: clickable Google Maps URI for business rows; null when none could
   * be built. Coalesced to null by the mapper (absent on pre-DEV-499b RPCs). */
  maps_uri: string | null;
  /** DEV-503: true when skills[] is an echo of our own search query. Nullable
   * here because a pre-DEV-503 RPC shape omits the column entirely. */
  skills_from_query: boolean | null;
  /** DEV-503: true when location_city/country came from the query fallback.
   * Nullable for the same reason as skills_from_query. */
  location_from_query: boolean | null;
  /** DEV-807: the member's own connected email channel, resolved live by the RPC
   * from user_contacts. Null for external rows and for members with no active
   * email channel; absent entirely on a pre-DEV-807 RPC shape. */
  member_email: string | null;
  /** DEV-822: the lead holding this person — matched by source_candidate_id, else
   * normalized email, else normalized phone. Null when none holds them; absent
   * entirely on a pre-DEV-822 RPC shape. */
  lead_id: string | null;
  /** DEV-822: when that lead was claimed, or null while it is still a prefill. */
  lead_claimed_at: string | null;
  /** DEV-TBD: true when an admin entered this candidate by hand. Absent on pre-migration RPC shapes. */
  manually_added: boolean | null;
  /** 20260923100000: referral provenance. Absent on pre-migration RPC shapes. */
  referred_by_user_id?: string | null;
  referred_by_name?: string | null;
  referral_code?: string | null;
  referral_note?: string | null;
  /** 20260922140000: past-volunteer evidence (jsonb). Absent on pre-migration RPC shapes. */
  volunteer_history?: unknown;
  total_count: number | null;
}

const VALID_LI_STATUSES = new Set<string>(LINKEDIN_OUTREACH_STATUSES);
/**
 * DEV-817: one `public.leads` row → `AdminLead`.
 *
 * `source` is narrowed against LEAD_SOURCES rather than cast: the column has a
 * CHECK constraint, but a frontend running against a database whose CHECK has
 * since gained a fifth source would otherwise hand the UI a value its switch
 * statements do not handle. Falling back to the row's raw value is not an option
 * for a typed union, so an unrecognised source reads as 'manual' — the source
 * that makes no provenance claim.
 */
/**
 * DEV-820: the RPC's report table → `LeadImportReport`.
 *
 * THE JOIN BACK TO CSV LINE NUMBERS LIVES HERE. `admin_import_leads` returns a
 * 0-based `row_index` into the payload it was handed, because line numbers are
 * the client's business — a quoted field may contain newlines, so CSV line ≠ row
 * ordinal and only `parseCsv` knows the mapping. Getting this join wrong is a
 * quiet failure: the report would show a real outcome next to another person's
 * line.
 *
 * SORTED BY INDEX, because a `SETOF` result carries no ordering guarantee and
 * the report is read top to bottom by a human.
 */
function buildLeadImportReport(
  rows: LeadImportRow[],
  data: unknown[] | null,
  dryRun: boolean,
): LeadImportReport {
  // `?? []` because PostgREST can answer a set-returning function with `null`
  // rather than `[]`; mapping over that would turn "nothing to import" into a
  // crash.
  const reported = (data ?? []) as Record<string, unknown>[];

  const results: LeadImportRowResult[] = [];
  for (const entry of [...reported].sort(
    (a, b) => Number(a.row_index ?? 0) - Number(b.row_index ?? 0),
  )) {
    const source = rows[Number(entry.row_index)];
    // An index we never submitted cannot be given a line number, and inventing
    // one would put a real outcome beside a fictional CSV line. Dropped instead.
    if (!source) continue;

    const raw = String(entry.outcome ?? "");
    // `hasOwnProperty`, NOT `in`. `in` walks the prototype chain, so `"toString"`
    // and `"constructor"` passed this guard and were written through as real
    // outcomes — and `groupLeadImportRows` then indexes LEAD_IMPORT_GROUPS with
    // them, resolves a function, and crashes on `grouped[<function>].push`. That
    // is the very row-loss this guard exists to stop, arriving as a crash.
    //
    // `Object.prototype.hasOwnProperty.call` rather than `Object.hasOwn`, which
    // is ES2022 while tsconfig's `lib` is ES2020.
    const known = Object.prototype.hasOwnProperty.call(LEAD_IMPORT_GROUPS, raw);
    results.push({
      line: source.line,
      // AN UNKNOWN OUTCOME BECOMES `error`, NOT A DROPPED ROW. A migration
      // adding an eleventh outcome must not make rows vanish from an older
      // client's report: a missing row reads as "nothing happened to this
      // person", which is the one answer that is always wrong.
      outcome: known ? (raw as LeadImportOutcome) : "error",
      leadId: (entry.lead_id as string | null) ?? null,
      message: known
        ? ((entry.message as string | null) ?? null)
        : `unrecognized outcome '${raw}' from the importer${entry.message ? `: ${entry.message}` : ""}`,
    });
  }

  return { dryRun, rows: results, counts: countLeadImport(results) };
}

function mapLeadRow(row: Record<string, unknown>): AdminLead {
  const source = row.source as LeadSource;
  return {
    id: row.id as string,
    email: (row.email as string | null) ?? null,
    phone: (row.phone as string | null) ?? null,
    displayName: (row.display_name as string) ?? "",
    locationCity: (row.location_city as string | null) ?? null,
    locationCountry: (row.location_country as string | null) ?? null,
    skills: (row.skills as string[] | null) ?? [],
    employmentTitle: (row.employment_title as string | null) ?? null,
    source: LEAD_SOURCES.includes(source) ? source : "manual",
    sourceCandidateId: (row.source_candidate_id as string | null) ?? null,
    candidateIdentityId: (row.candidate_identity_id as string | null) ?? null,
    createdBy: (row.created_by as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
    claimedBy: (row.claimed_by as string | null) ?? null,
    claimedAt: (row.claimed_at as string | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function narrowLinkedinStatus(v: string | null | undefined): LinkedinOutreachStatus {
  if (v != null && VALID_LI_STATUSES.has(v)) return v as LinkedinOutreachStatus;
  return "none";
}

/**
 * DEV-788. Defaults to "email", matching the outreach_drafts.channel DEFAULT:
 * during a deploy skew the RPC may omit the column, and every row that predates
 * it IS an email draft. Narrowing an unknown value to "email" rather than
 * passing it through also keeps an unexpected string out of the union.
 */
function narrowOutreachChannel(v: string | null | undefined): OutreachChannel {
  return v === "sms" || v === "whatsapp" ? v : "email";
}

const VALID_DISMISSAL_REASONS = new Set<string>(DISMISSAL_REASONS);
function narrowDismissalReason(v: string | null | undefined): DismissalReason | null {
  if (v != null && VALID_DISMISSAL_REASONS.has(v)) return v as DismissalReason;
  return null;
}

function mapCandidateRow(row: HeadhunterCandidateRow): AdminQuestCandidate {
  return {
    id: row.id,
    questId: row.quest_id,
    questTitle: row.quest_title,
    source: row.source,
    memberId: row.member_id,
    convertedUserId: row.converted_user_id,
    fullName: row.full_name ?? "Unknown",
    locationCity: row.location_city,
    locationCountry: row.location_country,
    skills: row.skills ?? [],
    employmentTitle: row.employment_title,
    linkedinUrl: row.linkedin_url,
    email: row.email,
    phone: row.phone,
    emailVerified: row.email_verified,
    score: row.score != null ? Number(row.score) : null,
    status: row.status,
    // Coalesce to null: a pre-outreach-migration RPC shape omits this column, so
    // the field arrives undefined — keep it null so the UI reads "needs outreach".
    outreachSentAt: row.outreach_sent_at ?? null,
    sourceUrl: row.source_url,
    collectedAt: row.collected_at,
    lawfulBasis: row.lawful_basis,
    purpose: row.purpose,
    lastRefreshedAt: row.last_refreshed_at,
    createdAt: row.created_at,
    linkedinOutreachStatus: narrowLinkedinStatus(row.linkedin_outreach_status),
    linkedinSentAt: row.linkedin_sent_at,
    dismissalReason: narrowDismissalReason(row.dismissal_reason),
    dismissalNote: row.dismissal_note ?? null,
    sourceUrls: row.source_urls ?? [],
    aiProfile: row.ai_profile ?? null,
    aiProfileGeneratedAt: row.ai_profile_generated_at ?? null,
    aiAssessment: row.ai_assessment ?? null,
    aiAssessmentGeneratedAt: row.ai_assessment_generated_at ?? null,
    resumeUrl: row.resume_url ?? null,
    // ce-code-review: `?? []` — AdminQuestCandidate.aiEnrichedFields is typed
    // string[], but the RPC returns null for a row that was never AI-enriched, so
    // without this the mapper hands back a null the type says cannot happen. Its
    // own test (SupabaseStaffingService.aiEnrich.test.ts, "defaults
    // aiEnrichedFields to [] when column is null/missing") asserts this and fails
    // deterministically in isolation without it.
    aiEnrichedFields: row.ai_enriched_fields ?? [],
    candidateIdentityId: row.candidate_identity_id ?? null,
    otherQuestCount: row.other_quest_count != null ? Number(row.other_quest_count) : 0,
    sourcePlatform: row.source_platform ?? null,
    socialProfiles: row.social_profiles ?? null,
    // DEV-474: rows predating the column read as null; the DB DEFAULT is
    // 'person', so mirror that rather than surfacing an absent value.
    entityType: row.entity_type === "business" ? "business" : "person",
    mapsPlaceId: row.maps_place_id ?? null,
    // DEV-499b: absent on pre-DEV-499b RPC shapes — keep null so the UI reads
    // "no address / no map link" rather than an undefined it must special-case.
    formattedAddress: row.formatted_address ?? null,
    mapsUri: row.maps_uri ?? null,
    // DEV-503: `?? false` mirrors the column's own NOT NULL DEFAULT FALSE, and is
    // the right read for an absent column too (a frontend deployed ahead of the
    // migration). The alternative — treating unknown as `true` — would relabel
    // every genuine FullEnrich skill and Maps place type as "our search terms"
    // during the skew window, which is a worse lie than the one being fixed:
    // it discards real evidence rather than merely failing to flag its absence.
    skillsFromQuery: row.skills_from_query ?? false,
    locationFromQuery: row.location_from_query ?? false,
    // DEV-807: `?? null` covers a frontend deployed ahead of the migration — the
    // column is simply absent from the RPC result, and null is the pre-DEV-807
    // rendering ("No contact"), which is a degradation rather than a lie.
    memberEmail: row.member_email ?? null,
    // DEV-822: `?? null` covers a frontend deployed ahead of the migration, the
    // same way memberEmail does — the columns are simply absent from the RPC
    // result, and null resolves to candidateLeadState "none", i.e. exactly the
    // pre-DEV-822 rendering (no badge, an enabled create button). A degradation
    // to the old behaviour rather than a wrong claim about a lead.
    leadId: row.lead_id ?? null,
    leadClaimedAt: row.lead_claimed_at ?? null,
    // DEV-TBD: `?? false` covers a frontend deployed ahead of the migration —
    // the column is simply absent from the RPC result, and false is the safe
    // default (treat as sweep-sourced, not manual).
    manuallyAdded: row.manually_added ?? false,
    // Keyed on the referrer id: absent (frontend ahead of the migration) or
    // null reads as "not referred", which is exactly today's rendering.
    referral: row.referred_by_user_id
      ? {
          referredByUserId: row.referred_by_user_id,
          referredByName: row.referred_by_name ?? null,
          code: row.referral_code ?? null,
          note: row.referral_note ?? null,
        }
      : null,
    // Narrowed, failing closed: absent (frontend ahead of the migration) or
    // malformed reads as "not a past volunteer", so no Invite button shows.
    volunteerHistory: parseVolunteerHistory(row.volunteer_history),
  };
}

interface HeadhunterRunRow {
  id: string;
  chain_id: number;
  quest_id: string | null;
  quest_title: string | null;
  status: HeadhunterRun["status"];
  kind: HeadhunterRun["kind"];
  skipped_reason: string | null;
  internal_found: number;
  external_found: number;
  contactable_found: number;
  candidates_persisted: number;
  spend_usdc: number;
  wallet_balance_after: number | null;
  duration_ms: number | null;
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
  work_context: HeadhunterRun["workContext"];
  query_label: HeadhunterRun["queryLabel"];
  spend_basis: CostBasis | null;
}

function mapRunRow(row: HeadhunterRunRow): HeadhunterRun {
  return {
    id: row.id,
    chainId: row.chain_id,
    questId: row.quest_id,
    questTitle: row.quest_title,
    status: row.status,
    kind: row.kind,
    skippedReason: row.skipped_reason,
    internalFound: row.internal_found,
    externalFound: row.external_found,
    contactableFound: row.contactable_found ?? 0,
    candidatesPersisted: row.candidates_persisted,
    spendUsdc: Number(row.spend_usdc),
    walletBalanceAfter: row.wallet_balance_after != null ? Number(row.wallet_balance_after) : null,
    durationMs: row.duration_ms,
    errorMessage: row.error_message,
    startedAt: row.started_at,
    finishedAt: row.finished_at,
    workContext: row.work_context ?? null,
    queryLabel: row.query_label ?? null,
    spendBasis: row.spend_basis ?? null,
  };
}

interface TestbedCandidateRow {
  id: string;
  run_id: string;
  ordinal: number;
  payload: SearchTestbedCandidate;
}

interface HeadhunterLogEventRow {
  id: string;
  chain_id: number;
  run_id: string | null;
  kind: HeadhunterLogEvent["kind"] | null;
  event: string;
  level: HeadhunterLogEvent["level"];
  message: string;
  fields: Record<string, unknown> | null;
  created_at: string;
}

function mapLogEventRow(row: HeadhunterLogEventRow): HeadhunterLogEvent {
  return {
    id: row.id,
    chainId: row.chain_id,
    runId: row.run_id,
    // Nullish-coalesced rather than asserted: a frontend deployed ahead of the
    // migration would read undefined here, and 'quest' is what the column's own
    // DEFAULT classifies every such row as anyway.
    kind: row.kind ?? "quest",
    event: row.event,
    level: row.level,
    message: row.message,
    fields: row.fields ?? {},
    createdAt: row.created_at,
  };
}

interface HeadhunterHealthRow {
  status: HeadhunterHealth["status"];
  chain_id: number;
  wallet_balance_usdc: number | null;
  daily_spend_usdc: number | null;
  daily_cap_usdc: number | null;
  version: string | null;
  last_error: string | null;
  last_seen_at: string | null;
  updated_at: string;
}

interface PendingQuestRow {
  quest_id: string;
  title: string;
  status: string;
  created_at: string;
  internal_count: number;
  external_count: number;
  invited_count: number;
  pending_count: number;
  dismissed_count: number;
  row_count: number | null;
}

const PENDING_QUEST_STATUSES = new Set<string>(["published", "negotiating"]);

function mapPendingQuestRow(row: PendingQuestRow): PendingQuest {
  if (!PENDING_QUEST_STATUSES.has(row.status)) {
    throw new Error(`Unexpected quest status from RPC: ${row.status}`);
  }
  return {
    questId: row.quest_id,
    title: row.title,
    status: row.status as QuestStatus,
    createdAt: row.created_at,
    internalCount: Number(row.internal_count),
    externalCount: Number(row.external_count),
    invitedCount: Number(row.invited_count),
    pendingCount: Number(row.pending_count),
    dismissedCount: Number(row.dismissed_count),
  };
}
